import { useRouter as usePagesRouter } from 'next/compat/router';
import { usePathname } from 'next/navigation';
import NProgress from 'nprogress';
import { useCallback, useEffect, useRef } from 'react';

NProgress.configure({ showSpinner: false });

const useNavigationProgress = () => {
  const startProgress = useCallback((step: number = 0) => {
    NProgress.start();
    NProgress.set(step);
  }, []);

  const endProgress = useCallback(() => {
    NProgress.done();
  }, []);

  const pageRouter = usePagesRouter?.(); // Will be undefined in the new App Router
  const pathname = usePathname(); // Works in App Router; undefined in pure Pages apps

  // 1) If we have an old page router with events, do the classic subscription
  useEffect(() => {
    if (pageRouter?.events) {
      pageRouter.events.on('routeChangeStart', startProgress);
      pageRouter.events.on('routeChangeComplete', endProgress);
      pageRouter.events.on('routeChangeError', endProgress);

      return () => {
        pageRouter.events.off('routeChangeStart', startProgress);
        pageRouter.events.off('routeChangeComplete', endProgress);
        pageRouter.events.off('routeChangeError', endProgress);
      };
    }
  }, [pageRouter]);

  // 2) Otherwise, fallback to detecting route changes via pathname in the new App Router
  const previousPathRef = useRef(pathname);
  useEffect(() => {
    // If there's no pageRouter.events but we do have a pathname,
    // we can detect when it changes:
    if (!pageRouter?.events && pathname !== null) {
      if (pathname !== previousPathRef.current) {
        // Start progress on new route
        startProgress();

        // End progress once the new route has presumably loaded
        // (We don't have an official "complete" event in the new router.)
        // A setTimeout is a crude approximation; adjust timing as needed.
        const handle = setTimeout(endProgress, 400);

        previousPathRef.current = pathname;

        return () => clearTimeout(handle);
      }
    }
  }, [pathname, pageRouter]);

  return { startProgress, endProgress };
};

export default useNavigationProgress;
